import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AcademicCapIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import { useUserHasAccess } from "../../../../routes/roleOnly";

/**
 * Quick Start Guide for Competency Management
 * Helps new users get started with competency management
 */
export const CompetencyQuickStart = ({ onDismiss }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  const steps = [
    {
      id: 1,
      title: t("Opprett kompetansedefinisjoner"),
      description: t("Definer hvilke typer kompetanse som finnes i organisasjonen"),
      icon: DocumentTextIcon,
      path: "/dashboard/hse-ks/competency/definitions/new",
      action: t("Opprett kompetanse"),
      available: canManage,
    },
    {
      id: 2,
      title: t("Opprett roller"),
      description: t("Definer roller og hvilke kompetanser som kreves for hver rolle"),
      icon: UserGroupIcon,
      path: "/dashboard/hse-ks/competency/roles/new",
      action: t("Opprett rolle"),
      available: canManage,
    },
    {
      id: 3,
      title: t("Tildel kompetanse til ansatte"),
      description: t("Legg til kompetanse for ansatte i organisasjonen"),
      icon: AcademicCapIcon,
      path: "/dashboard/hse-ks/competency/employees",
      action: t("Se ansatte"),
      available: canManage,
    },
    {
      id: 4,
      title: t("Se din egen kompetanse"),
      description: t("Se og administrer din egen kompetanse"),
      icon: CheckCircleIcon,
      path: "/dashboard/hse-ks/competency",
      action: t("Se mine kompetanser"),
      available: true,
    },
  ];

  const availableSteps = steps.filter(step => step.available);

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t("Kom i gang med kompetansehåndtering")}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t("Følg disse stegene for å sette opp kompetansehåndtering i organisasjonen")}
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {availableSteps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(step.path)}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <h4 className="text-sm font-medium text-gray-900">{step.title}</h4>
                      <p className="text-xs text-gray-500 mt-1">{step.description}</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(step.path);
                        }}
                        className="mt-3 inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        {step.action}
                        <ArrowRightIcon className="ml-1 h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

